import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const tourrettesSurLoupEn: ChauffeurPage = {
  slug: "tourrettes-sur-loup",
  title: "VTC & private chauffeur in Tourrettes-sur-Loup",
  subtitle:
    "Private chauffeur in Tourrettes-sur-Loup: the city of violets, a medieval hilltop village above the Loup gorges, and Nice airport transfers.",
  keywords: [
    "vtc tourrettes-sur-loup",
    "private chauffeur tourrettes-sur-loup",
    "tourrettes sur loup airport transfer",
    "loup gorges chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Tourrettes-sur-Loup",
    subtitle:
      "The city of violets, a medieval village perched above the Loup gorges: Nice airport transfers and hourly hire.",
    keywords: [
      "VTC Tourrettes-sur-Loup",
      "city of violets chauffeur",
      "Nice airport transfer",
      "Loup gorges chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Tourrettes-sur-Loup",
    badges: ["Medieval village", "City of violets", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Tourrettes-sur-Loup",
    items: [
      {
        title: "A preserved hilltop village",
        description:
          "The medieval lanes and ramparts are explored on foot: the chauffeur drops you at the village entrance and collects you on request, with no circling for a space.",
      },
      {
        title: "The city of violets",
        description:
          "Craft workshops, galleries and the emblematic flower: a leisurely day at the village pace, with a dedicated chauffeur who waits.",
      },
      {
        title: "Nice airport ~40 min",
        description:
          "Reach Nice Côte d’Azur airport in about 40 minutes via Vence and the plain, with flight tracking.",
      },
      {
        title: "Bilingual chauffeurs",
        description:
          "FR/EN, based on the French Riviera, punctual and discreet — 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Tourrettes-sur-Loup",
    services: [
      {
        title: "Nice airport transfers",
        text: "Pick-up in Tourrettes-sur-Loup with flight tracking, to and from Nice Côte d’Azur airport, in about 40 minutes.",
      },
      {
        title: "Village & galleries discovery",
        text: "Drop-off at the village entrance, waiting during the visit to the craft workshops and ramparts, pick-up on request.",
      },
      {
        title: "Hourly hire",
        text: "By the hour or the day: Vence, Saint-Paul-de-Vence, the Loup gorges and the neighbouring hilltop villages of the back country.",
      },
      {
        title: "Loup gorges outing",
        text: "A route towards Le Bar-sur-Loup, Gourdon and the waterfalls, with a chauffeur used to the winding roads of the hinterland.",
      },
      {
        title: "Long distance",
        text: "Journeys of any distance from Tourrettes-sur-Loup: Cannes, Monaco, Nice, Italy and ski resorts, in a comfortable vehicle.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Medieval village",
        description:
          "Hilltop heart, lanes, ramparts and Place de la Libération.",
      },
      {
        name: "Craft workshops & galleries",
        description:
          "Glass-blowers, painters and artisans of the city of violets.",
      },
      {
        name: "Loup gorges",
        description: "Wild valley, waterfalls and the road up to Gourdon.",
      },
      {
        name: "Vence",
        description: "Neighbouring town, Matisse chapel and historic centre.",
      },
      {
        name: "Saint-Paul-de-Vence",
        description: "Art village nearby, home to the Maeght Foundation.",
      },
      {
        name: "Nice airport",
        description: "About 40 minutes via Vence and the plain.",
      },
    ],
  },
  generic: {
    heading: "Tourrettes-sur-Loup, the city of violets",
    subheading:
      "A medieval village perched above the Loup gorges, between craft and back country.",
    paragraphs: [
      "Nicknamed the city of violets, Tourrettes-sur-Loup lines its houses like ramparts above the Loup gorges. Its medieval lanes, craft workshops and galleries are best explored on foot; the chauffeur drops you at the village entrance and waits while you wander, with no parking constraint.",
      "The village is also a gateway to the back country: Vence, Saint-Paul-de-Vence, Le Bar-sur-Loup, Gourdon and the waterfalls of the Loup gorges are all within reach by road. Our chauffeurs, at ease on the winding roads of the hinterland, shape the itinerary to your wishes.",
      "For Nice Côte d’Azur airport, allow around 40 minutes via Vence and the plain depending on traffic; our bilingual chauffeurs track your flight and adjust the departure accordingly.",
    ],
    bullets: [
      "Drop-off at the village entrance",
      "Back-country outings",
      "Flight tracking & instant quote",
    ],
  },
  process: {
    heading: "Book a chauffeur in Tourrettes-sur-Loup",
    steps: [
      "Share your route (village, Loup gorges, Nice airport) and your times.",
      "We confirm the right vehicle and a fixed fare or hourly hire.",
      "Your chauffeur tracks your flight and greets you at the agreed address.",
      "Live adjustments to suit traffic and your appetite for discovery.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Tourrettes-sur-Loup"),
    {
      q: "How long is it to Nice airport?",
      a: "Allow about 40 minutes via Vence and the plain depending on traffic, with flight tracking and a personal welcome.",
    },
    {
      q: "Can I visit the village and the Loup gorges with a chauffeur?",
      a: "Yes, we drop you at the village entrance and put together an outing in the Loup gorges (Le Bar-sur-Loup, Gourdon, waterfalls), waiting during your visits.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in high season and during the violet festival; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles do you offer?",
      a: "Mercedes E-Class and S-Class sedans, 7-seater van, SUV and VIP minibus, depending on the number of passengers and the occasion.",
    },
    {
      q: "Do you serve Vence and Saint-Paul-de-Vence?",
      a: "Yes, the neighbouring villages of Vence and Saint-Paul-de-Vence are a few minutes away; we provide transfers and hourly hire across the whole area.",
    },
  ],
  relatedLinks: [
    {
      label: "Private driver on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Transfers from Nice Airport", href: "/en/chauffeur/airport" },
    { label: "Airport transfers in Nice", href: "/en/chauffeur/nice" },
    { label: "Car hire in Vence", href: "/en/location/car-rental-vence" },
  ],
  cta: {
    title: "Your private chauffeur in Tourrettes-sur-Loup",
    text: "Nice airport transfer, village discovery or hourly hire — fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Tourrettes-sur-Loup — private chauffeur",
    description:
      "Private car service in Tourrettes-sur-Loup: Nice airport transfer in ~40 min, medieval village, Loup gorges. 5.0/5 from 38 reviews. Instant quote.",
    canonical: "/en/chauffeur/tourrettes-sur-loup",
  },
};

export default tourrettesSurLoupEn;
